import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Compass } from "lucide-react";

export const Survey = () => {
  const { user } = useAuth();
  const canApprove = ["surveyor_general", "sovereign_admin"].includes(user?.role);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["surveys"],
    queryFn: async () => (await api.get("/survey/records", { params: { limit: 200 } })).data,
  });
  const approve = useMutation({
    mutationFn: async (id) => (await api.post(`/survey/records/${id}/approve`)).data,
    onSuccess: () => { toast.success("Survey approved"); qc.invalidateQueries({ queryKey: ["surveys"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="page-survey">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Survey360™</div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">Survey Administration</h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1">Registered surveyors · diagrams · beacons · examination pipeline</div>
      </header>
      <div className="cad-panel overflow-hidden">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left border-b cad-hairline">
              {["Survey #","Surveyor","Province","District","Purpose","CRS","Status","Actions"].map((h) => (
                <th key={h} className="px-3 py-2.5 font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="cad-tbody">
            {isLoading && <tr><td colSpan={8} className="px-3 py-6 text-center text-[#7C93A5]">Loading…</td></tr>}
            {items.map((s) => (
              <tr key={s.id} data-testid={`survey-row-${s.id}`}>
                <td className="px-3 py-2 font-mono-cad text-[#F6F8FA]">{s.survey_number}</td>
                <td className="px-3 py-2 flex items-center gap-1.5"><Compass size={12} className="text-[#C99A2E]" /> {s.surveyor}</td>
                <td className="px-3 py-2">{s.province}</td>
                <td className="px-3 py-2">{s.district}</td>
                <td className="px-3 py-2 text-[#DCE3E8]">{s.purpose}</td>
                <td className="px-3 py-2 font-mono-cad text-[#B3C2CC]">{s.coordinate_system}</td>
                <td className="px-3 py-2">
                  <span className={`cad-chip ${s.status === "approved" ? "cad-chip-green" : s.status === "rejected" ? "cad-chip-red" : "cad-chip-blue"}`}>
                    {s.status.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-3 py-2">
                  {canApprove && s.status !== "approved" && (
                    <button data-testid={`approve-survey-${s.id}`} onClick={() => approve.mutate(s.id)}
                            className="cad-btn-outline text-[11px] px-2 py-1 rounded-sm inline-flex items-center gap-1">
                      <Check size={11} /> Approve
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
